import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ValueInputField } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';

import { useProjectTypeContext } from '@/features/remotion-editor/editor/contexts/project-type-context';
import {
  useCurrentStateAsRef,
  useFullState,
  useWriteContext,
} from '@/features/remotion-editor/editor/utils/use-context';

import {
  applyCaptionAdditionalDisplayTime,
  type CaptionAdditionalDisplayTimeFailureReason,
  getCaptionAdditionalDisplayTimeMs,
} from '../../../state/actions/apply-caption-additional-display-time';
import { useTargetItemIds } from '../../utils/use-target-item-ids';

const DISPLAY_TIME_STEP_SECONDS = 0.1;

const roundSeconds = (milliseconds: number): number =>
  Math.round(milliseconds) / 1000;

export const AdditionalDisplayTimeControls: React.FC<{
  itemId: string;
}> = ({ itemId }) => {
  const t = useTranslations();
  const state = useFullState();
  const stateAsRef = useCurrentStateAsRef();
  const { setState } = useWriteContext();
  const { projectType } = useProjectTypeContext();
  const targetItemIds = useTargetItemIds(itemId);
  const selectionKey = targetItemIds.join('|');

  const currentDisplayTime = useMemo(() => {
    const values = targetItemIds.map((targetItemId) =>
      getCaptionAdditionalDisplayTimeMs({
        state,
        itemId: targetItemId,
      }),
    );
    const validValues = values.filter(
      (value): value is number => value != null,
    );

    if (
      validValues.length !== targetItemIds.length ||
      validValues.length === 0
    ) {
      return { seconds: 0, isMixed: true };
    }

    const firstValue = validValues[0]!;
    return {
      seconds: roundSeconds(firstValue),
      isMixed: validValues.some((value) => value !== firstValue),
    };
  }, [state, targetItemIds]);

  const [draftSeconds, setDraftSeconds] = useState<number | null>(
    currentDisplayTime.seconds,
  );
  const [isDirty, setIsDirty] = useState(false);
  const [failureReason, setFailureReason] =
    useState<CaptionAdditionalDisplayTimeFailureReason | null>(null);

  useEffect(() => {
    setDraftSeconds(currentDisplayTime.seconds);
    setIsDirty(false);
    setFailureReason(null);
  }, [currentDisplayTime.isMixed, currentDisplayTime.seconds, selectionKey]);

  const handleDraftChange = useCallback((value: number | string) => {
    setFailureReason(null);
    setIsDirty(true);
    setDraftSeconds(typeof value === 'number' ? value : null);
  }, []);

  const handleApply = useCallback(() => {
    if (draftSeconds == null) return;

    const result = applyCaptionAdditionalDisplayTime({
      state: stateAsRef.current,
      itemIds: targetItemIds,
      additionalDisplaySeconds: draftSeconds,
      projectType,
    });

    if (!result.ok) {
      setFailureReason(result.reason);
      return;
    }

    if (result.changedItemIds.length > 0) {
      setState({
        update: result.state,
        commitToUndoStack: true,
      });
    }

    setFailureReason(null);
    setIsDirty(false);
  }, [draftSeconds, projectType, setState, stateAsRef, targetItemIds]);

  const failureMessage = failureReason
    ? t(`Editor.inspector.caption-display-time.errors.${failureReason}`)
    : null;

  return (
    <div className="flex flex-col gap-4xs">
      <ValueInputField
        isShowLabel={true}
        label={t('Editor.inspector.caption-display-time.label')}
        accessibilityLabel={t(
          'Editor.inspector.caption-display-time.accessibility-label',
        )}
        value={draftSeconds ?? undefined}
        onChange={handleDraftChange}
        min={0}
        max={null}
        step={DISPLAY_TIME_STEP_SECONDS}
        isUnit="s"
        enableDrag={false}
        hasMixedValue={currentDisplayTime.isMixed && !isDirty}
        mixedLabel={t('Editor.inspector.caption-display-time.mixed')}
        className="w-full"
      />
      <p className="text-caption-small text-sys-cont-neutral-light">
        {t('Editor.inspector.caption-display-time.description')}
      </p>
      {failureMessage && (
        <p
          role="alert"
          className="text-caption-small text-sys-cont-negative-default"
        >
          {failureMessage}
        </p>
      )}
      <Button
        type="button"
        size="small"
        hierarchy="tertiary"
        onClick={handleApply}
        state={isDirty && draftSeconds != null ? 'default' : 'disabled'}
        disabled={!isDirty || draftSeconds == null}
        className="w-full"
      >
        {t('Editor.inspector.caption-display-time.apply', {
          count: targetItemIds.length,
        })}
      </Button>
    </div>
  );
};
